import { useEffect, useSyncExternalStore } from "react";
import { Hub } from "aws-amplify/utils";
import { resolveTbrReader, listTbr, saveTbr, deleteTbr } from "./tbrApi";

// Shared in-memory view of the reader's server shelf. Never persists account data locally.
const listeners = new Set();
let state = { books: [], status: "idle", error: "", readerSub: null, pending: {} };
let epoch = 0;
let queue = Promise.resolve();
let refreshPromise;
const mutations = new Map();
const publish = patch => { state = { ...state, ...patch }; listeners.forEach(listener => listener()); };
const enqueue = task => { const result = queue.then(task); queue = result.catch(() => {}); return result; };

export function normalizeTbrBook(book, { productUrl, coverImageUrl } = {}) {
  let url = "";
  try {
    const source = productUrl || book.productUrl;
    if (source) {
      const parsed = new URL(source, window.location.origin);
      if (parsed.protocol === "https:" && !parsed.username && !parsed.password) {
        parsed.search = "";
        parsed.hash = "";
        url = parsed.href.replace(/\/$/, "");
      }
    }
  } catch { /* The save button explains when a product link is unavailable. */ }
  const prices = (Array.isArray(book.formats) ? book.formats : []).map(f => f?.price).concat(book.price)
    .filter(p => p !== null && p !== undefined && p !== "" && Number.isFinite(Number(p)) && Number(p) >= 0).map(Number);
  return {
    title: book.title || "Untitled Book", authorName: book.authorName || "Unknown Author",
    coverImageUrl: coverImageUrl || book.coverImageUrl || "", productUrl: url,
    price: prices.length ? Math.min(...prices) : null,
  };
}
function resetSession() {
  epoch += 1;
  refreshPromise = null;
  mutations.clear();
  publish({ books: [], status: "idle", error: "", readerSub: null, pending: {} });
  if (listeners.size) refreshTbr().catch(() => {});
}
Hub.listen("auth", ({ payload }) => {
  if (["signedIn", "signedOut"].includes(payload.event)) resetSession();
});
async function withReader(task, expectedOwner) {
  const currentEpoch = epoch;
  try {
    const reader = await resolveTbrReader();
    if (currentEpoch !== epoch) return;
    if (expectedOwner && reader.readerSub !== expectedOwner) throw Object.assign(new Error("Your Reader account changed. Refresh your shelf before continuing."), { code: "READER_ACCOUNT_CHANGED" });
    if (state.readerSub !== reader.readerSub) publish({ books: [], readerSub: reader.readerSub, status: "loading", error: "" });
    const result = await task(reader);
    if (currentEpoch !== epoch) return;
    const current = await resolveTbrReader();
    if (current.readerSub !== reader.readerSub) throw Object.assign(new Error("Your Reader account changed. Refresh your shelf before continuing."), { code: "READER_ACCOUNT_CHANGED" });
    return currentEpoch === epoch && state.readerSub === reader.readerSub ? result : undefined;
  } catch (error) {
    if (currentEpoch !== epoch) return;
    if (error.code === "READER_SIGN_IN_REQUIRED") publish({ books: [], status: "guest", readerSub: null, error: "" });
    if (error.code === "READER_ACCOUNT_CHANGED") publish({ books: [], status: "error", readerSub: null, error: error.message });
    throw error;
  }
}
export function refreshTbr() {
  if (refreshPromise) return refreshPromise;
  const currentEpoch = epoch;
  publish({ status: state.books.length ? state.status : "loading", error: "" });
  const promise = enqueue(async () => {
    if (currentEpoch !== epoch) return;
    try {
      const books = await withReader(listTbr);
      if (books && currentEpoch === epoch) publish({ books, status: "ready", error: "" });
    } catch (error) {
      if (currentEpoch === epoch && error.code !== "READER_SIGN_IN_REQUIRED") publish({ status: "error", error: error.message });
    }
  });
  refreshPromise = promise;
  promise.finally(() => { if (refreshPromise === promise) refreshPromise = null; });
  return promise;
}
function mutate(key, action) {
  if (mutations.has(key)) return mutations.get(key);
  const currentEpoch = epoch;
  const expectedOwner = state.readerSub;
  publish({ pending: { ...state.pending, [key]: true } });
  const promise = enqueue(async () => {
    if (currentEpoch !== epoch) return;
    const result = await withReader(async reader => {
      if (state.status !== "ready") {
        const books = await listTbr(reader);
        if (currentEpoch !== epoch) return;
        publish({ books, status: "ready", error: "" });
      }
      return action(reader);
    }, expectedOwner);
    if (result && currentEpoch === epoch) publish({ books: result, status: "ready", error: "" });
  }).catch(error => {
    if (currentEpoch === epoch && state.status === "loading" && error.code !== "READER_SIGN_IN_REQUIRED") {
      publish({ status: "error", error: error.message });
    }
    throw error;
  }).finally(() => {
    if (currentEpoch !== epoch) return;
    mutations.delete(key);
    const pending = { ...state.pending };
    delete pending[key];
    publish({ pending });
  });
  mutations.set(key, promise);
  return promise;
}
export function addToTbr(book) {
  if (!book.productUrl) return Promise.reject(new Error("This book's purchase link is not available yet."));
  return mutate(book.productUrl, async reader => {
    const saved = await saveTbr(reader, book);
    return [saved, ...state.books.filter(item => item.id !== saved.id)];
  });
}
export function removeFromTbr(id) {
  return mutate(id, async reader => { await deleteTbr(reader, id); return state.books.filter(book => book.id !== id); });
}
function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
let stopRefresh;
export function useTbr() {
  const snapshot = useSyncExternalStore(subscribe, () => state, () => state);
  useEffect(() => {
    if (state.status === "idle") refreshTbr();
    if (!stopRefresh) {
      const refresh = () => { if (document.visibilityState === "visible") refreshTbr(); };
      window.addEventListener("focus", refresh);
      const timer = window.setInterval(refresh, 60000);
      stopRefresh = () => { window.removeEventListener("focus", refresh); window.clearInterval(timer); stopRefresh = null; };
    }
    return () => { if (!listeners.size && stopRefresh) stopRefresh(); };
  }, []);
  return snapshot;
}
